'use client';

import { type ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { useLabsRuntime } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { labsDraftStorageKey, type SwipeDirection } from '@/app/(platform)/(subscription-gated)/labs/_engine/store';
import { getPreviewVisitorId } from './anonymous-id';
import { applyOpeningAnswer } from './opening-answer';
import { PREVIEW_STEPS, PREVIEW_STORE_VARIANT, PREVIEW_TERMINAL_STEP } from './preview-steps';

/**
 * Restores a returning reader's draft. The anonymous cousin of `LabsContainer`, with one deliberate
 * difference that matters more than everything else in this file.
 *
 * 🔴 IT PAINTS IMMEDIATELY. IT DOES NOT GATE RENDER ON HYDRATION.
 *
 * The sprint's container returns `null` until the persisted draft has loaded, which is right there:
 * every user is signed in, most have a draft, and a flash of the wrong step is worse than a beat of
 * nothing. Copying that here was a mistake, and a measurable one. Because the gate sat above the whole
 * tree, the prerendered HTML for this route contained NO LAB AT ALL. 33KB of script tags and not one
 * word of content. A student on a phone on campus wifi, tapping a link from a Google result, got a
 * white screen until the JavaScript downloaded, parsed and hydrated.
 *
 * For this audience that is the worst possible first impression, and it is self-inflicted: the draft
 * is a returning-visitor convenience, and almost nobody here is a returning visitor. So the tree
 * renders straight away with the store's default state, which is `step: 'welcome'` — exactly what a
 * first-time reader should see — and the draft is restored underneath.
 *
 * WHAT THIS COSTS, honestly: a reader who left mid-lab and comes back sees the welcome screen for one
 * frame before jumping to where they were. That is a real regression for a small group, traded for a
 * first paint for everyone. On a public page reached from search, that trade is not close.
 *
 * The visitor id is no longer resolved here either. It is a synchronous localStorage read, so
 * `PreviewApp` takes it in a lazy initializer and every event carries it from the first render. This
 * container owning it was the reason it had to withhold children in the first place.
 */
/**
 * Whether a persisted draft represents a run the reader is part-way through.
 *
 * False for no draft, for a draft still on the first step (opened the page, did nothing), and for one
 * that already reached the payoff (finished; a new opening answer should start a new run). True only
 * for the middle, which is the state an opening answer must never overwrite.
 *
 * Written defensively because the value comes out of `localStorage`, where anything could be: a shape
 * this code does not recognise is treated as "no run in progress", which at worst re-seeds a draft that
 * could not have been read anyway.
 */
function isRunInProgress(storedValue: unknown): boolean {
  const step = (storedValue as {
    state?: {
      step?: unknown;
    };
  } | null)?.state?.step;
  if (typeof step !== 'string') {
    return false;
  }
  return step !== PREVIEW_STEPS[0] && step !== PREVIEW_TERMINAL_STEP;
}

/** Exposed for `preview-container.spec.ts`. Not part of the component's API. */
export const __testing = {
  isRunInProgress
};
export function PreviewContainer({
  children,
  openingAnswer
}: {
  children: ReactNode;
  /** The `?a1=` answer given on the guide page, or null for a plain link or a direct hit. */
  openingAnswer: SwipeDirection | null;
}): ReactNode {
  const {
    pack,
    store
  } = useLabsRuntime();

  // 🔴 `useLayoutEffect`, AND BOTH HALVES OF THAT CHOICE ARE LOAD-BEARING.
  //
  // Not during render: this route is PRERENDERED, so the server HTML is the welcome screen for every
  // visitor regardless of query string. Seeding while rendering would make the client's first render
  // disagree with that HTML, and React answers a hydration mismatch by throwing the server markup away
  // and re-rendering the subtree on the client. That would discard the very first paint this whole
  // container exists to protect, to fix a flash — a strictly worse trade than the flash.
  //
  // Not `useEffect` either: that runs after the browser paints, so the reader who just answered the
  // question on the guide page would watch the welcome screen appear and then be jumped to card two.
  // A layout effect runs after hydration commits and BEFORE paint, so the reader sees card two and
  // nothing else. Same pattern, same reason as the sprint's `use-welcome-skip.ts`.
  //
  // The ref latches it to once per mount, so a reader who presses Back to the welcome screen on purpose
  // is not shoved forward again on the next render.
  const seededRef = useRef(false);
  useLayoutEffect(() => {
    if (!openingAnswer || seededRef.current) {
      return;
    }
    seededRef.current = true;

    // 🔴 SCOPE THE KEY, THEN CHECK THE DRAFT, THEN SEED. All three, in that order, and each ordering
    // mistake here has already shipped once.
    //
    // Scoping first: the seed used to run against the store's DEFAULT key, leaving an orphan
    // `labs-{vertical}-preview-v1` entry holding the reader's answers that nothing ever read or
    // cleaned. Worth not creating with a SOC 2 audit dated 2026-09-01.
    //
    // Checking before seeding: scoping and then seeding immediately writes the opening answer straight
    // over a real saved draft, because zustand persists on every `set`. That is the refresh bug all
    // over again, since `?a1=` stays in the address bar for the whole session, so every reload would
    // throw a reader four screens in back to card two. Found by Bugbot on #2843, having been fixed once
    // already and reintroduced by the orphan-key fix.
    //
    // The read is synchronous because this is `localStorage`, which is what lets the whole decision
    // happen before paint rather than in the passive effect below.
    const visitorId = getPreviewVisitorId();
    const storageKey = visitorId ? `${labsDraftStorageKey(pack, PREVIEW_STORE_VARIANT)}:${visitorId}` : null;
    if (storageKey) {
      store.persist.setOptions({
        name: storageKey
      });
      let saved: unknown = null;
      try {
        const raw = window.localStorage.getItem(storageKey);
        saved = raw === null ? null : JSON.parse(raw);
      } catch {
        // Unparseable storage is treated as no draft, which re-seeds something unreadable anyway.
        saved = null;
      }

      // A run in progress always wins. The passive effect below rehydrates it.
      if (isRunInProgress(saved)) {
        return;
      }
    }
    applyOpeningAnswer(store, pack, openingAnswer);
  }, [openingAnswer, pack, store]);
  useEffect(() => {
    let isMounted = true;
    const visitorId_0 = getPreviewVisitorId();

    // No storage means no draft to restore and nothing to scope. The default state is already correct.
    if (!visitorId_0) {
      return;
    }
    const persistApi = store.persist;
    const storageKey_0 = `${labsDraftStorageKey(pack, PREVIEW_STORE_VARIANT)}:${visitorId_0}`;
    persistApi.setOptions({
      name: storageKey_0
    });
    const {
      storage
    } = persistApi.getOptions();

    // Only rehydrate when a draft actually exists. The sprint's container has to reset first, because
    // a cached store there could hold a DIFFERENT user's answers. Here the store is fresh and the
    // default is what we are already showing, so resetting would only risk clearing a live run.
    Promise.resolve(storage?.getItem(storageKey_0)).then(storedValue => {
      if (!isMounted) {
        return;
      }

      // 🔴 AN OPENING ANSWER BEATS AN UNSTARTED DRAFT, NOT A RUN IN PROGRESS. (The seed itself has
      // already been applied under the correctly scoped key by the layout effect above; this
      // re-application only matters when a draft turns out not to be worth restoring.)
      //
      // This used to re-seed whenever `?a1=` was present, full stop. The reasoning was that a reader
      // who just answered a question on the guide page has told us what they want, seconds ago, and
      // restoring a week-old draft over that would make their click appear to do nothing.
      //
      // That reasoning is right about a fresh ARRIVAL and wrong about a REFRESH, which is the far
      // commoner event. The `?a1=` parameter stays in the address bar for the whole session, so every
      // reload re-ran the seed and skipped rehydration: a reader four screens into the lab who
      // refreshed, rotated their phone, or came back to the tab was silently thrown back to card two
      // with their answers dropped. Found by Bugbot on #2843.
      //
      // So the seed only wins when there is nothing to lose: no draft at all, a draft still sitting on
      // the welcome screen, or a draft that already reached the payoff (a returning finisher who
      // answered a NEW question on a NEW guide page, which is a genuinely new run).
      if (openingAnswer && !isRunInProgress(storedValue)) {
        // Re-applied rather than merely kept. Layout effects run before passive ones, so the seed
        // above landed against the store's DEFAULT persist key; the scoped key is only known here.
        // `applyOpeningAnswer` is idempotent, so this cannot half-apply.
        applyOpeningAnswer(store, pack, openingAnswer);
        return;
      }
      if (storedValue == null) {
        return;
      }
      return Promise.resolve(persistApi.rehydrate());
    }).catch(() => undefined);
    return () => {
      isMounted = false;
    };
  }, [openingAnswer, pack, store]);
  return children;
}